import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../../components/layout/Layout';

interface FormData {
  nome: string;
  descricao: string;
  objetivo: string;
  dataInicio: string;
  dataFim: string;
  publico: string[];
  status: string;
}

export default function DadosBasicosPage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<FormData>({
    nome: '',
    descricao: '',
    objetivo: '',
    dataInicio: '',
    dataFim: '',
    publico: [],
    status: 'rascunho'
  });

  const [editorMode, setEditorMode] = useState('write'); // 'write' ou 'preview'
  const [selectedVersion, setSelectedVersion] = useState<number | null>(null); // Versão selecionada para edição
  const [versionDescriptions, setVersionDescriptions] = useState<Record<number, string>>({
    1: 'Versão inicial do regulamento',
    2: 'Atualização das regras de pontuação',
    3: 'Inclusão de novas categorias de premiação',
    4: 'Revisão completa do regulamento'
  });

  // Estado para FAQ
  const [faqItems, setFaqItems] = useState([
    {
      id: 1,
      pergunta: 'Como posso participar da campanha?',
      resposta: 'Para participar da campanha, você deve estar cadastrado no sistema e atender aos critérios de elegibilidade definidos no regulamento.',
      editando: false
    },
    {
      id: 2,
      pergunta: 'Qual é o período de vigência da campanha?',
      resposta: 'A campanha terá duração conforme as datas definidas nos dados básicos, com início e fim claramente especificados.',
      editando: false
    }
  ]);
  const [novoFaqItem, setNovoFaqItem] = useState({ pergunta: '', resposta: '' });
  const [mostrarFormularioFaq, setMostrarFormularioFaq] = useState(false);
  const [faqEditandoId, setFaqEditandoId] = useState<number | null>(null);

  // Histórico de versões do regulamento
  const [historicoRegulamento] = useState([
    {
      id: 1,
      versao: '1.0',
      dataHora: '2024-03-15 14:30:00',
      autor: 'João Silva',
      descricao: 'Versão inicial do regulamento',
      tamanho: '2.5 KB',
      status: 'publicado' // publicado, pendente
    },
    {
      id: 2,
      versao: '1.1',
      dataHora: '2024-03-16 10:15:00',
      autor: 'Maria Santos',
      descricao: 'Atualização das regras de pontuação',
      tamanho: '2.8 KB',
      status: 'publicado'
    },
    {
      id: 3,
      versao: '1.2',
      dataHora: '2024-03-17 16:45:00',
      autor: 'Carlos Oliveira',
      descricao: 'Inclusão de novas categorias de premiação',
      tamanho: '3.1 KB',
      status: 'publicado'
    },
    {
      id: 4,
      versao: '2.0',
      dataHora: '2024-03-18 09:20:00',
      autor: 'Ana Costa',
      descricao: 'Revisão completa do regulamento',
      tamanho: '3.5 KB',
      status: 'pendente' // Versão atual pendente de publicação
    }
  ]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Dados da campanha:', formData);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePublicoToggle = (tipo: string) => {
    setFormData(prev => ({
      ...prev,
      publico: prev.publico.includes(tipo)
        ? prev.publico.filter(p => p !== tipo)
        : [...prev.publico, tipo]
    }));
  };

  const handleRegulamentoChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      regulamento: e.target.value
    });
  };

  const handleProximo = () => {
    navigate('/admin/incentivo/campanhas/objetivos');
  };

  const handleVoltar = () => {
    navigate('/admin/incentivo/campanhas');
  };

  // Funções de formatação do editor - CORRIGIDAS
  const insertText = (before: string, after: string = '') => {
    const textarea = document.getElementById('regulamento-editor') as HTMLTextAreaElement;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = textarea.value.substring(start, end);
    const beforeText = textarea.value.substring(0, start);
    const afterText = textarea.value.substring(end);

    let newText;
    let newPosition;

    if (selectedText) {
      // Se há texto selecionado, aplica a formatação
      newText = beforeText + before + selectedText + after + afterText;
      newPosition = start + before.length + selectedText.length + after.length;
    } else {
      // Se não há seleção, insere o texto de exemplo
      const exampleText = before === '**' ? 'texto em negrito' : 
                         before === '*' ? 'texto em itálico' :
                         before === '<u>' ? 'texto sublinhado' :
                         before === '## ' ? 'Título Principal' :
                         before === '### ' ? 'Subtítulo' :
                         before === '• ' ? 'item da lista' :
                         before === '1. ' ? 'primeiro item' :
                         before === '[texto do link](url)' ? '' : 'texto';
      
      if (before === '[texto do link](url)') {
        newText = beforeText + before + afterText;
        newPosition = start + before.length;
      } else {
        newText = beforeText + before + exampleText + after + afterText;
        newPosition = start + before.length + exampleText.length + after.length;
      }
    }
    
    setFormData({
      ...formData,
      regulamento: newText
    });

    // Restaurar foco e posição do cursor
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(newPosition, newPosition);
    }, 10);
  };

  const formatBold = () => insertText('**', '**');
  const formatItalic = () => insertText('*', '*');
  const formatUnderline = () => insertText('<u>', '</u>');
  const insertHeading = () => {
    const textarea = document.getElementById('regulamento-editor') as HTMLTextAreaElement;
    if (!textarea) return;
    
    const start = textarea.selectionStart;
    const beforeText = textarea.value.substring(0, start);
    const afterText = textarea.value.substring(start);
    
    // Adiciona quebra de linha se não estiver no início da linha
    const prefix = beforeText.endsWith('\n') || beforeText === '' ? '' : '\n';
    insertText(prefix + '## ');
  };
  
  const insertSubheading = () => {
    const textarea = document.getElementById('regulamento-editor') as HTMLTextAreaElement;
    if (!textarea) return;
    
    const start = textarea.selectionStart;
    const beforeText = textarea.value.substring(0, start);
    
    const prefix = beforeText.endsWith('\n') || beforeText === '' ? '' : '\n';
    insertText(prefix + '### ');
  };
  
  const insertList = () => {
    const textarea = document.getElementById('regulamento-editor') as HTMLTextAreaElement;
    if (!textarea) return;
    
    const start = textarea.selectionStart;
    const beforeText = textarea.value.substring(0, start);
    
    const prefix = beforeText.endsWith('\n') || beforeText === '' ? '' : '\n';
    insertText(prefix + '• ');
  };
  
  const insertNumberedList = () => {
    const textarea = document.getElementById('regulamento-editor') as HTMLTextAreaElement;
    if (!textarea) return;
    
    const start = textarea.selectionStart;
    const beforeText = textarea.value.substring(0, start);
    
    const prefix = beforeText.endsWith('\n') || beforeText === '' ? '' : '\n';
    insertText(prefix + '1. ');
  };
  
  const insertLink = () => insertText('[texto do link](url)');

  const insertTemplate = () => {
    const template = `## REGULAMENTO DA CAMPANHA

### 1. OBJETIVO
Definir as regras e condições para participação na campanha de incentivo.

### 2. PARTICIPANTES
• Todos os colaboradores elegíveis conforme critérios estabelecidos
• Período de vigência da campanha
• Requisitos mínimos de participação

### 3. METAS E PONTUAÇÃO
• Descrição das metas a serem atingidas
• Sistema de pontuação
• Critérios de avaliação

### 4. PREMIAÇÃO
• Tipos de prêmios disponíveis
• Critérios para distribuição
• Forma de entrega dos prêmios

### 5. REGRAS GERAIS
• Condições de participação
• Penalidades por descumprimento
• Casos especiais

### 6. DISPOSIÇÕES FINAIS
• Alterações no regulamento
• Casos omissos
• Vigência do regulamento`;

    setFormData({
      ...formData,
      regulamento: template
    });
    
    // Focar no editor após inserir template
    setTimeout(() => {
      const textarea = document.getElementById('regulamento-editor') as HTMLTextAreaElement;
      if (textarea) {
        textarea.focus();
        textarea.setSelectionRange(template.length, template.length);
      }
    }, 10);
  };

  // Renderizar preview do markdown - MELHORADO
  const renderPreview = (text: string) => {
    if (!text.trim()) return '';
    
    return text
      .replace(/## (.*)/g, '<h2 class="text-xl font-bold text-gray-900 mb-3 mt-6 first:mt-0">$1</h2>')
      .replace(/### (.*)/g, '<h3 class="text-lg font-semibold text-gray-800 mb-2 mt-4">$1</h3>')
      .replace(/\*\*(.*?)\*\*/g, '<strong class="font-bold">$1</strong>')
      .replace(/\*(.*?)\*/g, '<em class="italic">$1</em>')
      .replace(/<u>(.*?)<\/u>/g, '<u class="underline">$1</u>')
      .replace(/^• (.*)/gm, '<li class="ml-4 list-disc list-inside">$1</li>')
      .replace(/^\d+\. (.*)/gm, '<li class="ml-4 list-decimal list-inside">$1</li>')
      .replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" class="text-blue-600 hover:underline">$1</a>')
      .replace(/\n\n/g, '</p><p class="mb-4">')
      .replace(/\n/g, '<br>')
      .replace(/^(.*)/, '<p class="mb-4">$1')
      .replace(/(.*$)/, '$1</p>');
  };

  const handlePublicarRegulamento = (versao: string) => {
    console.log('Publicar regulamento versão:', versao);
    // Implementar publicação do regulamento
    alert(`Regulamento versão ${versao} publicado com sucesso!`);
  };

  const handleCompararVersoes = (versaoId: number) => {
    console.log('Comparar versão:', versaoId);
    // Implementar comparação de versões
  };

  const handleEditarVersao = (versaoId: number) => {
    const versao = historicoRegulamento.find(v => v.id === versaoId);
    if (versao) {
      setSelectedVersion(versaoId);
      
      // Se a versão está publicada, apenas visualiza (modo preview)
      // Se a versão está pendente, permite edição (modo write)
      const modoEdicao = versao.status === 'publicado' ? 'preview' : 'write';
      setEditorMode(modoEdicao);
      
      // Simular carregamento do conteúdo da versão selecionada
      const conteudoVersao = `## REGULAMENTO DA CAMPANHA - Versão ${versao.versao}

### 1. OBJETIVO
Conteúdo específico da versão ${versao.versao} do regulamento.

### 2. PARTICIPANTES
• Regras específicas desta versão
• Critérios atualizados em ${new Date(versao.dataHora).toLocaleDateString('pt-BR')}

### 3. ALTERAÇÕES
${versao.descricao}

### 4. METAS E PONTUAÇÃO
• Sistema de pontuação atualizado
• Critérios de avaliação revisados

### 5. PREMIAÇÃO
• Tipos de prêmios disponíveis
• Critérios para distribuição atualizados

### 6. REGRAS GERAIS
• Condições de participação
• Penalidades por descumprimento
• Casos especiais

### 7. DISPOSIÇÕES FINAIS
• Alterações no regulamento
• Casos omissos
• Vigência do regulamento

*Última modificação por: ${versao.autor} em ${new Date(versao.dataHora).toLocaleString('pt-BR')}*`;

      setFormData({
        ...formData,
        regulamento: conteudoVersao
      });

      // Focar no editor após um pequeno delay para garantir que o DOM foi atualizado
      setTimeout(() => {
        if (modoEdicao === 'write') {
          const textarea = document.getElementById('regulamento-editor') as HTMLTextAreaElement;
          if (textarea) {
            textarea.focus();
            textarea.setSelectionRange(0, 0);
            textarea.scrollTop = 0;
          }
        }
      }, 100);

      // Scroll para o editor/preview
      setTimeout(() => {
        const targetElement = modoEdicao === 'write' 
          ? document.getElementById('regulamento-editor')
          : document.querySelector('.prose'); // Seletor do preview
        
        if (targetElement) {
          targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 150);
    }
  };

  const handleCancelarEdicao = () => {
    setSelectedVersion(null);
    setFormData({
      ...formData,
      regulamento: ''
    });
  };

  const handleSalvarVersao = () => {
    if (selectedVersion) {
      const versao = historicoRegulamento.find(v => v.id === selectedVersion);
      if (versao) {
        console.log('Salvando alterações na versão:', versao.versao);
        alert(`Alterações na versão ${versao.versao} salvas com sucesso!`);
        setSelectedVersion(null);
      }
    } else {
      // Salvar como nova versão
      console.log('Salvando nova versão do regulamento');
      alert('Nova versão do regulamento salva com sucesso!');
    }
  };

  const handleVersionDescriptionChange = (versionId: number, newDescription: string) => {
    setVersionDescriptions(prev => ({
      ...prev,
      [versionId]: newDescription
    }));
  };

  // Funções para FAQ
  const handleAdicionarFaq = () => {
    if (novoFaqItem.pergunta.trim() && novoFaqItem.resposta.trim()) {
      const novoId = Math.max(...faqItems.map(item => item.id), 0) + 1;
      setFaqItems([...faqItems, {
        id: novoId,
        pergunta: novoFaqItem.pergunta.trim(),
        resposta: novoFaqItem.resposta.trim(),
        editando: false
      }]);
      setNovoFaqItem({ pergunta: '', resposta: '' });
      setMostrarFormularioFaq(false);
    }
  };

  const handleEditarFaq = (id: number) => {
    setFaqItems(faqItems.map(item => 
      item.id === id ? { ...item, editando: true } : { ...item, editando: false }
    ));
    setFaqEditandoId(id);
  };

  const handleSalvarFaq = (id: number, pergunta: string, resposta: string) => {
    if (pergunta.trim() && resposta.trim()) {
      setFaqItems(faqItems.map(item => 
        item.id === id 
          ? { ...item, pergunta: pergunta.trim(), resposta: resposta.trim(), editando: false }
          : item
      ));
      setFaqEditandoId(null);
    }
  };

  const handleCancelarEdicaoFaq = (id: number) => {
    setFaqItems(faqItems.map(item => 
      item.id === id ? { ...item, editando: false } : item
    ));
    setFaqEditandoId(null);
  };

  const handleExcluirFaq = (id: number) => {
    if (confirm('Tem certeza que deseja excluir esta pergunta frequente?')) {
      setFaqItems(faqItems.filter(item => item.id !== id));
    }
  };

  const handleCancelarNovoFaq = () => {
    setNovoFaqItem({ pergunta: '', resposta: '' });
    setMostrarFormularioFaq(false);
  };

  // Funções de formatação para FAQ
  const insertFaqText = (textareaId: string, before: string, after: string = '') => {
    const textarea = document.getElementById(textareaId) as HTMLTextAreaElement;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = textarea.value.substring(start, end);
    const beforeText = textarea.value.substring(0, start);
    const afterText = textarea.value.substring(end);

    let newText;
    let newPosition;

    if (selectedText) {
      newText = beforeText + before + selectedText + after + afterText;
      newPosition = start + before.length + selectedText.length + after.length;
    } else {
      const exampleText = before === '**' ? 'texto em negrito' : 
                         before === '*' ? 'texto em itálico' :
                         before === '<u>' ? 'texto sublinhado' :
                         before === '• ' ? 'item da lista' :
                         before === '1. ' ? 'primeiro item' :
                         before === '[texto do link](url)' ? '' : 'texto';
      
      if (before === '[texto do link](url)') {
        newText = beforeText + before + afterText;
        newPosition = start + before.length;
      } else {
        newText = beforeText + before + exampleText + after + afterText;
        newPosition = start + before.length + exampleText.length + after.length;
      }
    }
    
    textarea.value = newText;
    textarea.focus();
    textarea.setSelectionRange(newPosition, newPosition);
    
    // Trigger change event
    const event = new Event('input', { bubbles: true });
    textarea.dispatchEvent(event);
  };

  const formatFaqBold = (textareaId: string) => insertFaqText(textareaId, '**', '**');
  const formatFaqItalic = (textareaId: string) => insertFaqText(textareaId, '*', '*');
  const formatFaqUnderline = (textareaId: string) => insertFaqText(textareaId, '<u>', '</u>');
  const insertFaqList = (textareaId: string) => {
    const textarea = document.getElementById(textareaId) as HTMLTextAreaElement;
    if (!textarea) return;
    
    const start = textarea.selectionStart;
    const beforeText = textarea.value.substring(0, start);
    
    const prefix = beforeText.endsWith('\n') || beforeText === '' ? '' : '\n';
    insertFaqText(textareaId, prefix + '• ');
  };
  
  const insertFaqNumberedList = (textareaId: string) => {
    const textarea = document.getElementById(textareaId) as HTMLTextAreaElement;
    if (!textarea) return;
    
    const start = textarea.selectionStart;
    const beforeText = textarea.value.substring(0, start);
    
    const prefix = beforeText.endsWith('\n') || beforeText === '' ? '' : '\n';
    insertFaqText(textareaId, prefix + '1. ');
  };
  
  const insertFaqLink = (textareaId: string) => insertFaqText(textareaId, '[texto do link](url)');

  // Renderizar preview do markdown para FAQ
  const renderFaqPreview = (text: string) => {
    if (!text.trim()) return '';
    
    return text
      .replace(/\*\*(.*?)\*\*/g, '<strong class="font-bold">$1</strong>')
      .replace(/\*(.*?)\*/g, '<em class="italic">$1</em>')
      .replace(/<u>(.*?)<\/u>/g, '<u class="underline">$1</u>')
      .replace(/^• (.*)/gm, '<li class="ml-4 list-disc list-inside">$1</li>')
      .replace(/^\d+\. (.*)/gm, '<li class="ml-4 list-decimal list-inside">$1</li>')
      .replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" class="text-blue-600 hover:underline">$1</a>')
      .replace(/\n\n/g, '</p><p class="mb-2">')
      .replace(/\n/g, '<br>')
      .replace(/^(.*)/, '<p class="mb-2">$1')
      .replace(/(.*$)/, '$1</p>');
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Dados Básicos da Campanha</h1>
            <p className="text-gray-600 mt-1">Configure as informações principais da sua campanha de incentivo</p>
          </div>
          <div className="flex items-center space-x-3">
            <button className="px-4 py-2 text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors cursor-pointer whitespace-nowrap">
              Cancelar
            </button>
            <button 
              onClick={handleSubmit}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              Salvar Rascunho
            </button>
          </div>
        </div>

        {/* Progress Steps */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-semibold">
                1
              </div>
              <span className="font-semibold text-gray-900">Dados Básicos</span>
            </div>
            <div className="flex-1 h-1 bg-gray-200 mx-4"></div>
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 rounded-full bg-gray-200 text-gray-600 flex items-center justify-center font-semibold">
                2
              </div>
              <span className="text-gray-600">Objetivos</span>
            </div>
            <div className="flex-1 h-1 bg-gray-200 mx-4"></div>
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 rounded-full bg-gray-200 text-gray-600 flex items-center justify-center font-semibold">
                3
              </div>
              <span className="text-gray-600">Ações</span>
            </div>
            <div className="flex-1 h-1 bg-gray-200 mx-4"></div>
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 rounded-full bg-gray-200 text-gray-600 flex items-center justify-center font-semibold">
                4
              </div>
              <span className="text-gray-600">Regras</span>
            </div>
            <div className="flex-1 h-1 bg-gray-200 mx-4"></div>
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 rounded-full bg-gray-200 text-gray-600 flex items-center justify-center font-semibold">
                5
              </div>
              <span className="text-gray-600">Premiação</span>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Informações Gerais */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Informações Gerais</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nome da Campanha *
                </label>
                <input
                  type="text"
                  name="nome"
                  value={formData.nome}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  placeholder="Ex: Campanha de Vendas Q4 2024"
                  required
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Descrição
                </label>
                <textarea
                  name="descricao"
                  value={formData.descricao}
                  onChange={handleChange}
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  placeholder="Descreva os objetivos e detalhes da campanha..."
                ></textarea>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tipo de Campanha *
                </label>
                <select
                  name="tipo"
                  value={formData.tipo}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm pr-8"
                  required
                >
                  <option value="">Selecione o tipo</option>
                  <option value="vendas">Vendas</option>
                  <option value="performance">Performance</option>
                  <option value="aquisicao">Aquisição de Clientes</option>
                  <option value="capacitacao">Capacitação</option>
                  <option value="engajamento">Engajamento</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Categoria
                </label>
                <select
                  name="categoria"
                  value={formData.categoria}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm pr-8"
                >
                  <option value="">Selecione a categoria</option>
                  <option value="trimestral">Trimestral</option>
                  <option value="mensal">Mensal</option>
                  <option value="sazonal">Sazonal</option>
                  <option value="especial">Especial</option>
                </select>
              </div>
            </div>
          </div>

          {/* Período */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Período de Vigência</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Data de Início *
                </label>
                <input
                  type="date"
                  name="dataInicio"
                  value={formData.dataInicio}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Data de Término *
                </label>
                <input
                  type="date"
                  name="dataFim"
                  value={formData.dataFim}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  required
                />
              </div>
            </div>
          </div>

          {/* Público-Alvo */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Público-Alvo</h3>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Tipo de Premiado *
              </label>
              <p className="text-xs text-gray-500 mb-4">Selecione um ou mais tipos de premiados</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                {[
                  { id: 'pf', label: 'Pessoa Física (PF)', icon: 'ri-user-line', color: 'blue' },
                  { id: 'pj', label: 'Pessoa Jurídica (PJ)', icon: 'ri-building-line', color: 'emerald' },
                  { id: 'mei', label: 'MEI', icon: 'ri-store-line', color: 'purple' },
                  { id: 'clt', label: 'Funcionário CLT', icon: 'ri-briefcase-line', color: 'orange' },
                  { id: 'autonomo', label: 'Prestador autônomo', icon: 'ri-user-star-line', color: 'pink' },
                  { id: 'representante', label: 'Representante comercial', icon: 'ri-user-voice-line', color: 'indigo' },
                  { id: 'distribuidor', label: 'Distribuidor / revendedor', icon: 'ri-truck-line', color: 'teal' }
                ].map((tipo) => (
                  <button
                    key={tipo.id}
                    type="button"
                    onClick={() => handlePublicoToggle(tipo.id)}
                    className={`
                      relative p-4 rounded-lg border-2 transition-all duration-200 text-left
                      ${formData.publico.includes(tipo.id)
                        ? `border-${tipo.color}-500 bg-${tipo.color}-50`
                        : 'border-gray-200 bg-white hover:border-gray-300'
                      }
                    `}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`
                        w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0
                        ${formData.publico.includes(tipo.id)
                          ? `bg-${tipo.color}-500 text-white`
                          : `bg-${tipo.color}-100 text-${tipo.color}-600`
                        }
                      `}>
                        <i className={`${tipo.icon} text-lg`}></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`
                          text-sm font-medium
                          ${formData.publico.includes(tipo.id) ? 'text-gray-900' : 'text-gray-700'}
                        `}>
                          {tipo.label}
                        </p>
                      </div>
                      {formData.publico.includes(tipo.id) && (
                        <div className={`absolute top-2 right-2 w-5 h-5 rounded-full bg-${tipo.color}-500 flex items-center justify-center`}>
                          <i className="ri-check-line text-white text-xs"></i>
                        </div>
                      )}
                    </div>
                  </button>
                ))}
              </div>
              
              {formData.publico.length === 0 && (
                <p className="text-xs text-red-500 mt-2">
                  <i className="ri-error-warning-line mr-1"></i>
                  Selecione pelo menos um tipo de premiado
                </p>
              )}
              
              {formData.publico.length > 0 && (
                <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-xs font-medium text-blue-900 mb-2">
                    <i className="ri-information-line mr-1"></i>
                    Tipos selecionados ({formData.publico.length}):
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {formData.publico.map((id) => {
                      const tipo = [
                        { id: 'pf', label: 'Pessoa Física (PF)', color: 'blue' },
                        { id: 'pj', label: 'Pessoa Jurídica (PJ)', color: 'emerald' },
                        { id: 'mei', label: 'MEI', color: 'purple' },
                        { id: 'clt', label: 'Funcionário CLT', color: 'orange' },
                        { id: 'autonomo', label: 'Prestador autônomo', color: 'pink' },
                        { id: 'representante', label: 'Representante comercial', color: 'indigo' },
                        { id: 'distribuidor', label: 'Distribuidor / revendedor', color: 'teal' }
                      ].find(t => t.id === id);
                      
                      return tipo ? (
                        <span
                          key={id}
                          className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-${tipo.color}-100 text-${tipo.color}-700`}
                        >
                          {tipo.label}
                        </span>
                      ) : null;
                    })}
                  </div>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                >
                  <option value="rascunho">Rascunho</option>
                  <option value="programada">Programada</option>
                  <option value="ativa">Ativa</option>
                  <option value="pausada">Pausada</option>
                  <option value="finalizada">Finalizada</option>
                </select>
              </div>
            </div>
          </div>

          {/* FAQ da Campanha */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Perguntas Frequentes (FAQ)</h3>
                <p className="text-sm text-gray-600 mt-1">Configure as perguntas e respostas mais comuns sobre a campanha</p>
              </div>
              <button
                type="button"
                onClick={() => setMostrarFormularioFaq(true)}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap flex items-center space-x-2"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-add-line"></i>
                </div>
                <span>Adicionar FAQ</span>
              </button>
            </div>

            {/* Lista de FAQ Items */}
            <div className="space-y-4">
              {faqItems.map((item, index) => (
                <div key={item.id} className="border border-gray-200 rounded-lg p-4">
                  {!item.editando ? (
                    /* Modo Visualização */
                    <div>
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-start space-x-3 flex-1">
                          <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                            <span className="text-sm font-bold text-blue-600">{index + 1}</span>
                          </div>
                          <div className="flex-1">
                            <h4 className="text-sm font-semibold text-gray-900 mb-2">{item.pergunta}</h4>
                            <div 
                              className="text-sm text-gray-700 prose prose-sm max-w-none"
                              dangerouslySetInnerHTML={{ __html: renderFaqPreview(item.resposta) }}
                            ></div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2 ml-4">
                          <button
                            type="button"
                            onClick={() => handleEditarFaq(item.id)}
                            className="text-gray-400 hover:text-blue-600 cursor-pointer"
                            title="Editar FAQ"
                          >
                            <div className="w-5 h-5 flex items-center justify-center">
                              <i className="ri-edit-line"></i>
                            </div>
                          </button>
                          <button
                            type="button"
                            onClick={() => handleExcluirFaq(item.id)}
                            className="text-gray-400 hover:text-red-600 cursor-pointer"
                            title="Excluir FAQ"
                          >
                            <div className="w-5 h-5 flex items-center justify-center">
                              <i className="ri-delete-bin-line"></i>
                            </div>
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    /* Modo Edição */
                    <FaqEditForm
                      item={item}
                      onSave={handleSalvarFaq}
                      onCancel={handleCancelarEdicaoFaq}
                      formatBold={formatFaqBold}
                      formatItalic={formatFaqItalic}
                      formatUnderline={formatFaqUnderline}
                      insertList={insertFaqList}
                      insertNumberedList={insertFaqNumberedList}
                      insertLink={insertFaqLink}
                    />
                  )}
                </div>
              ))}

              {faqItems.length === 0 && (
                <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-lg">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <div className="w-8 h-8 flex items-center justify-center">
                      <i className="ri-question-line text-gray-400 text-2xl"></i>
                    </div>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Nenhuma pergunta frequente criada</h3>
                  <p className="text-gray-600 mb-4">Adicione perguntas e respostas para esclarecer dúvidas sobre a campanha</p>
                  <button
                    type="button"
                    onClick={() => setMostrarFormularioFaq(true)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Criar Primeira FAQ
                  </button>
                </div>
              )}
            </div>

            {/* Formulário de Nova FAQ */}
            {mostrarFormularioFaq && (
              <div className="mt-6 border-t border-gray-200 pt-6">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-lg font-semibold text-blue-900">Nova Pergunta Frequente</h4>
                    <button
                      type="button"
                      onClick={handleCancelarNovoFaq}
                      className="text-blue-400 hover:text-blue-600 cursor-pointer"
                    >
                      <div className="w-6 h-6 flex items-center justify-center">
                        <i className="ri-close-line text-xl"></i>
                      </div>
                    </button>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-blue-900 mb-2">
                        Pergunta *
                      </label>
                      <input
                        type="text"
                        value={novoFaqItem.pergunta}
                        onChange={(e) => setNovoFaqItem({ ...novoFaqItem, pergunta: e.target.value })}
                        className="w-full px-4 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                        placeholder="Digite a pergunta..."
                        maxLength={200}
                      />
                      <div className="text-xs text-blue-600 mt-1">
                        {novoFaqItem.pergunta.length}/200 caracteres
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-blue-900 mb-2">
                        Resposta *
                      </label>
                      
                      {/* Toolbar de Formatação para Nova FAQ */}
                      <div className="border border-blue-300 rounded-lg p-3 bg-white mb-2">
                        <div className="flex items-center space-x-2 flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() => formatFaqBold('nova-faq-resposta')}
                            className="px-3 py-2 bg-white border border-gray-300 rounded hover:bg-gray-50 text-sm font-bold cursor-pointer whitespace-nowrap flex items-center space-x-1"
                            title="Negrito"
                          >
                            <div className="w-4 h-4 flex items-center justify-center">
                              <i className="ri-bold"></i>
                            </div>
                            <span className="text-xs">B</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => formatFaqItalic('nova-faq-resposta')}
                            className="px-3 py-2 bg-white border border-gray-300 rounded hover:bg-gray-50 text-sm italic cursor-pointer whitespace-nowrap flex items-center space-x-1"
                            title="Itálico"
                          >
                            <div className="w-4 h-4 flex items-center justify-center">
                              <i className="ri-italic"></i>
                            </div>
                            <span className="text-xs">I</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => formatFaqUnderline('nova-faq-resposta')}
                            className="px-3 py-2 bg-white border border-gray-300 rounded hover:bg-gray-50 text-sm underline cursor-pointer whitespace-nowrap flex items-center space-x-1"
                            title="Sublinhado"
                          >
                            <div className="w-4 h-4 flex items-center justify-center">
                              <i className="ri-underline"></i>
                            </div>
                            <span className="text-xs">U</span>
                          </button>
                          <div className="w-px h-6 bg-gray-300"></div>
                          <button
                            type="button"
                            onClick={() => insertFaqList('nova-faq-resposta')}
                            className="px-3 py-2 bg-white border border-gray-300 rounded hover:bg-gray-50 text-sm cursor-pointer whitespace-nowrap flex items-center space-x-1"
                            title="Lista com Marcadores"
                          >
                            <div className="w-4 h-4 flex items-center justify-center">
                              <i className="ri-list-unordered"></i>
                            </div>
                          </button>
                          <button
                            type="button"
                            onClick={() => insertFaqNumberedList('nova-faq-resposta')}
                            className="px-3 py-2 bg-white border border-gray-300 rounded hover:bg-gray-50 text-sm cursor-pointer whitespace-nowrap flex items-center space-x-1"
                            title="Lista Numerada"
                          >
                            <div className="w-4 h-4 flex items-center justify-center">
                              <i className="ri-list-ordered"></i>
                            </div>
                          </button>
                          <button
                            type="button"
                            onClick={() => insertFaqLink('nova-faq-resposta')}
                            className="px-3 py-2 bg-white border border-gray-300 rounded hover:bg-gray-50 text-sm cursor-pointer whitespace-nowrap flex items-center space-x-1"
                            title="Inserir Link"
                          >
                            <div className="w-4 h-4 flex items-center justify-center">
                              <i className="ri-link"></i>
                            </div>
                          </button>
                        </div>
                      </div>

                      <textarea
                        id="nova-faq-resposta"
                        value={novoFaqItem.resposta}
                        onChange={(e) => setNovoFaqItem({ ...novoFaqItem, resposta: e.target.value })}
                        rows={6}
                        className="w-full px-4 py-3 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm font-mono resize-none"
                        placeholder="Digite a resposta...

Dicas de formatação:
**Texto em negrito**
*Texto em itálico*
<u>Texto sublinhado</u>
• Item da lista
1. Item numerado
[texto do link](url)"
                        maxLength={1000}
                      />
                      <div className="text-xs text-blue-600 mt-1">
                        {novoFaqItem.resposta.length}/1000 caracteres
                      </div>
                    </div>

                    {/* Preview da Nova FAQ */}
                    {novoFaqItem.resposta && (
                      <div>
                        <label className="block text-sm font-medium text-blue-900 mb-2">
                          Prévia da Resposta
                        </label>
                        <div className="border border-blue-300 rounded-lg p-3 bg-white">
                          <div 
                            className="text-sm text-gray-700 prose prose-sm max-w-none"
                            dangerouslySetInnerHTML={{ __html: renderFaqPreview(novoFaqItem.resposta) }}
                          ></div>
                        </div>
                      </div>
                    )}

                    <div className="flex items-center justify-end space-x-3 pt-4">
                      <button
                        type="button"
                        onClick={handleCancelarNovoFaq}
                        className="px-4 py-2 text-blue-700 bg-white border border-blue-300 hover:bg-blue-50 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                      >
                        Cancelar
                      </button>
                      <button
                        type="button"
                        onClick={handleAdicionarFaq}
                        disabled={!novoFaqItem.pergunta.trim() || !novoFaqItem.resposta.trim()}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Adicionar FAQ
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Dicas de Uso */}
            <div className="mt-6 bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <div className="w-5 h-5 flex items-center justify-center mt-0.5">
                  <i className="ri-lightbulb-line text-green-600"></i>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-green-900 mb-2">Dicas para um FAQ Eficaz</h4>
                  <div className="text-sm text-green-800 space-y-1">
                    <p>• <strong>Seja claro e direto:</strong> Use linguagem simples e objetiva</p>
                    <p>• <strong>Antecipe dúvidas:</strong> Pense nas perguntas mais comuns dos participantes</p>
                    <p>• <strong>Use formatação:</strong> Destaque informações importantes com negrito ou listas</p>
                    <p>• <strong>Mantenha atualizado:</strong> Revise e atualize as respostas conforme necessário</p>
                    <p>• <strong>Organize por prioridade:</strong> Coloque as perguntas mais importantes primeiro</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Regulamento da Campanha */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Regulamento da Campanha</h3>
              <div className="flex items-center space-x-2">
                <button
                  type="button"
                  onClick={() => {
                    setFormData({
                      ...formData,
                      regulamento: ''
                    });
                    setEditorMode('write');
                  }}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap flex items-center space-x-2"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-add-line"></i>
                  </div>
                  <span>Novo</span>
                </button>
                <button
                  type="button"
                  onClick={() => setEditorMode('preview')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap flex items-center space-x-2 ${
                    editorMode === 'preview' 
                      ? 'bg-blue-600 text-white shadow-sm' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-eye-line"></i>
                  </div>
                  <span>Visualizar</span>
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Editor - 2/3 da largura */}
              <div className="lg:col-span-2">
                {editorMode === 'write' ? (
                  <div className="space-y-4">
                    {/* Toolbar de Formatação */}
                    <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                      <div className="flex items-center space-x-2 flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={formatBold}
                          className="px-3 py-2 bg-white border border-gray-300 rounded hover:bg-gray-50 text-sm font-bold cursor-pointer whitespace-nowrap flex items-center space-x-1"
                          title="Negrito (Ctrl+B)"
                        >
                          <div className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-bold"></i>
                          </div>
                          <span className="text-xs">B</span>
                        </button>
                        <button
                          type="button"
                          onClick={formatItalic}
                          className="px-3 py-2 bg-white border border-gray-300 rounded hover:bg-gray-50 text-sm italic cursor-pointer whitespace-nowrap flex items-center space-x-1"
                          title="Itálico (Ctrl+I)"
                        >
                          <div className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-italic"></i>
                          </div>
                          <span className="text-xs">I</span>
                        </button>
                        <button
                          type="button"
                          onClick={formatUnderline}
                          className="px-3 py-2 bg-white border border-gray-300 rounded hover:bg-gray-50 text-sm underline cursor-pointer whitespace-nowrap flex items-center space-x-1"
                          title="Sublinhado (Ctrl+U)"
                        >
                          <div className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-underline"></i>
                          </div>
                          <span className="text-xs">U</span>
                        </button>
                        <div className="w-px h-6 bg-gray-300"></div>
                        <button
                          type="button"
                          onClick={insertHeading}
                          className="px-3 py-2 bg-white border border-gray-300 rounded hover:bg-gray-50 text-sm cursor-pointer whitespace-nowrap flex items-center space-x-1"
                          title="Título Principal"
                        >
                          <div className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-h-1"></i>
                          </div>
                          <span className="text-xs">H1</span>
                        </button>
                        <button
                          type="button"
                          onClick={insertSubheading}
                          className="px-3 py-2 bg-white border border-gray-300 rounded hover:bg-gray-50 text-sm cursor-pointer whitespace-nowrap flex items-center space-x-1"
                          title="Subtítulo"
                        >
                          <div className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-h-2"></i>
                          </div>
                          <span className="text-xs">H2</span>
                        </button>
                        <div className="w-px h-6 bg-gray-300"></div>
                        <button
                          type="button"
                          onClick={insertList}
                          className="px-3 py-2 bg-white border border-gray-300 rounded hover:bg-gray-50 text-sm cursor-pointer whitespace-nowrap flex items-center space-x-1"
                          title="Lista com Marcadores"
                        >
                          <div className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-list-unordered"></i>
                          </div>
                        </button>
                        <button
                          type="button"
                          onClick={insertNumberedList}
                          className="px-3 py-2 bg-white border border-gray-300 rounded hover:bg-gray-50 text-sm cursor-pointer whitespace-nowrap flex items-center space-x-1"
                          title="Lista Numerada"
                        >
                          <div className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-list-ordered"></i>
                          </div>
                        </button>
                        <button
                          type="button"
                          onClick={insertLink}
                          className="px-3 py-2 bg-white border border-gray-300 rounded hover:bg-gray-50 text-sm cursor-pointer whitespace-nowrap flex items-center space-x-1"
                          title="Inserir Link"
                        >
                          <div className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-link"></i>
                          </div>
                        </button>
                        <div className="w-px h-6 bg-gray-300"></div>
                        <button
                          type="button"
                          onClick={insertTemplate}
                          className="px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm cursor-pointer whitespace-nowrap flex items-center space-x-2"
                          title="Usar Template Padrão"
                        >
                          <div className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-file-text-line"></i>
                          </div>
                          <span>Template</span>
                        </button>
                      </div>
                    </div>

                    {/* Editor de Texto */}
                    <div>
                      <textarea
                        id="regulamento-editor"
                        name="regulamento"
                        value={formData.regulamento}
                        onChange={handleRegulamentoChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm font-mono resize-none h-96 overflow-y-auto"
                        placeholder="Digite o regulamento da campanha aqui...

Dicas de formatação:
## Título Principal
### Subtítulo
**Texto em negrito**
*Texto em itálico*
<u>Texto sublinhado</u>
• Item de lista
1. Item numerado
[texto do link](url)

Use o botão 'Template' para inserir uma estrutura completa de regulamento."
                      ></textarea>
                    </div>

                    {/* Dicas de Formatação */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-start space-x-3">
                        <div className="w-5 h-5 flex items-center justify-center mt-0.5">
                          <i className="ri-lightbulb-line text-blue-600"></i>
                        </div>
                        <div>
                          <h4 className="text-sm font-semibold text-blue-900 mb-2">Dicas de Formatação</h4>
                          <div className="text-sm text-blue-800 space-y-1">
                            <p><code className="bg-blue-100 px-1 rounded">## Título</code> - Cria um título principal</p>
                            <p><code className="bg-blue-100 px-1 rounded">### Subtítulo</code> - Cria um subtítulo</p>
                            <p><code className="bg-blue-100 px-1 rounded">**texto**</code> - Deixa o texto em negrito</p>
                            <p><code className="bg-blue-100 px-1 rounded">*texto*</code> - Deixa o texto em itálico</p>
                            <p><code className="bg-blue-100 px-1 rounded">• item</code> - Cria uma lista com marcadores</p>
                            <p><code className="bg-blue-100 px-1 rounded">1. item</code> - Cria uma lista numerada</p>
                            <p><code className="bg-blue-100 px-1 rounded">[texto](url)</code> - Cria um link</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  /* Preview do Regulamento */
                  <div className="border border-gray-200 rounded-lg p-6 bg-white h-96 overflow-y-auto">
                    {formData.regulamento ? (
                      <div 
                        className="prose prose-sm max-w-none text-gray-900"
                        dangerouslySetInnerHTML={{ 
                          __html: renderPreview(formData.regulamento) 
                        }}
                      ></div>
                    ) : (
                      <div className="text-center py-12">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                          <div className="w-8 h-8 flex items-center justify-center">
                            <i className="ri-file-text-line text-gray-400 text-2xl"></i>
                          </div>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Nenhum regulamento criado</h3>
                        <p className="text-gray-600 mb-4">Clique em "Editar" para começar a escrever o regulamento da campanha</p>
                        <button
                          type="button"
                          onClick={() => setEditorMode('write')}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap"
                        >
                          Começar a Escrever
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Timeline de Versões - 1/3 da largura */}
              <div className="lg:col-span-1">
                <div className="border border-gray-200 rounded-lg bg-gray-50 h-full">
                  <div className="p-4 border-b border-gray-200 bg-white rounded-t-lg">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-semibold text-gray-900">Histórico de Versões</h4>
                      <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center">
                        <span className="text-xs font-bold text-blue-600">{historicoRegulamento.length}</span>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Versões anteriores do regulamento</p>
                  </div>

                  <div className="p-4 max-h-[600px] overflow-y-auto">
                    {historicoRegulamento.length > 0 ? (
                      <div className="space-y-4">
                        {historicoRegulamento.map((versao, index) => (
                          <div key={versao.id} className="relative">
                            {/* Timeline Line */}
                            {index < historicoRegulamento.length - 1 && (
                              <div className="absolute left-4 top-10 bottom-0 w-0.5 bg-gray-300"></div>
                            )}

                            {/* Version Card */}
                            <div 
                              onClick={() => handleEditarVersao(versao.id)}
                              className={`border rounded-lg p-3 transition-all relative cursor-pointer ${
                                versao.status === 'publicado' 
                                  ? 'bg-gray-50 border-gray-200 hover:shadow-md hover:border-gray-300' 
                                  : selectedVersion === versao.id 
                                    ? 'bg-blue-50 border-blue-300 shadow-md ring-2 ring-blue-200' 
                                    : 'bg-white border-gray-200 hover:shadow-md hover:border-gray-300'
                              }`}
                            >
                              {/* Selection Indicator - apenas para versões não publicadas */}
                              {selectedVersion === versao.id && versao.status !== 'publicado' && (
                                <div className="absolute -top-2 -right-2 w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center shadow-lg">
                                  <div className="w-3 h-3 flex items-center justify-center">
                                    <i className="ri-check-line text-white text-xs"></i>
                                  </div>
                                </div>
                              )}

                              {/* Version Badge */}
                              <div className="flex items-start space-x-3 mb-2">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                                  versao.status === 'publicado' ? 'bg-green-500' :
                                  selectedVersion === versao.id ? 'bg-blue-600' :
                                  versao.status === 'pendente' ? 'bg-yellow-500' : 'bg-gray-400'
                                }`}>
                                  <div className="w-4 h-4 flex items-center justify-center">
                                    <i className={`${
                                      versao.status === 'publicado' ? 'ri-check-line' :
                                      versao.status === 'pendente' ? 'ri-time-line' : 'ri-file-text-line'
                                    } text-white text-sm`}></i>
                                  </div>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center justify-between mb-1">
                                    <span className={`text-sm font-bold ${
                                      versao.status === 'publicado' ? 'text-green-600' :
                                      selectedVersion === versao.id ? 'text-blue-600' :
                                      versao.status === 'pendente' ? 'text-yellow-600' : 'text-gray-900'
                                    }`}>
                                      v{versao.versao}
                                    </span>
                                    <div className="flex items-center space-x-2">
                                      {selectedVersion === versao.id && versao.status !== 'publicado' && (
                                        <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                                          Editando
                                        </span>
                                      )}
                                      {versao.status === 'pendente' && selectedVersion !== versao.id && (
                                        <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs font-medium rounded-full">
                                          Pendente
                                        </span>
                                      )}
                                      {versao.status === 'publicado' && (
                                        <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                                          Publicado
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  <p className={`text-xs ${versao.status === 'publicado' ? 'text-gray-400' : 'text-gray-500'} mt-1`}>
                                    {new Date(versao.dataHora).toLocaleString('pt-BR')}
                                  </p>
                                  <p className={`text-xs ${versao.status === 'publicado' ? 'text-gray-500' : 'text-gray-600'} mb-1`}>
                                    Por: <span className="font-medium">{versao.autor}</span>
                                  </p>
                                </div>
                              </div>

                              {/* Description Input Field */}
                              <div className="pl-11 mb-3">
                                <label className={`block text-xs font-medium ${versao.status === 'publicado' ? 'text-gray-500' : 'text-gray-700'} mb-1`}>
                                  Descrição da Versão
                                </label>
                                {versao.status === 'publicado' ? (
                                  <div className="w-full px-3 py-2 border border-gray-200 bg-gray-100 text-gray-600 rounded-lg text-xs min-h-[60px] cursor-not-allowed">
                                    {versionDescriptions[versao.id] || versao.descricao}
                                  </div>
                                ) : (
                                  <textarea
                                    value={versionDescriptions[versao.id] || versao.descricao}
                                    onChange={(e) => handleVersionDescriptionChange(versao.id, e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 bg-white text-gray-700 rounded-lg text-xs min-h-[60px] resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="Descreva as alterações desta versão..."
                                    maxLength={200}
                                  />
                                )}
                                <div className="flex items-center justify-between mt-1">
                                  <span className={`text-xs ${versao.status === 'publicado' ? 'text-gray-400' : 'text-gray-500'}`}>
                                    {(versionDescriptions[versao.id] || versao.descricao).length}/200 caracteres
                                  </span>
                                  {versao.status === 'publicado' && (
                                    <span className="text-xs text-gray-400 flex items-center space-x-1">
                                      <div className="w-3 h-3 flex items-center justify-center">
                                        <i className="ri-lock-line"></i>
                                      </div>
                                      <span>Somente leitura</span>
                                    </span>
                                  )}
                                </div>
                              </div>

                              {/* File Info */}
                              <div className="flex items-center justify-between pl-11 mb-3">
                                <div className={`flex items-center space-x-1 text-xs ${versao.status === 'publicado' ? 'text-gray-400' : 'text-gray-500'}`}>
                                  <div className="w-3 h-3 flex items-center justify-center">
                                    <i className="ri-file-line"></i>
                                  </div>
                                  <span>{versao.tamanho}</span>
                                </div>
                              </div>

                              {/* Actions - apenas para versões não publicadas */}
                              {versao.status !== 'publicado' && (
                                <div className="pl-11 space-y-2">
                                  <div className="flex items-center space-x-2">
                                    {/* Botão Publicar - aparece para versões pendentes */}
                                    {versao.status === 'pendente' && (
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handlePublicarRegulamento(versao.versao);
                                        }}
                                        className="flex-1 px-2 py-1.5 bg-green-50 hover:bg-green-100 text-green-700 rounded text-xs font-medium transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center space-x-1"
                                        title="Publicar versão"
                                      >
                                        <div className="w-3 h-3 flex items-center justify-center">
                                          <i className="ri-send-plane-line"></i>
                                        </div>
                                        <span>Publicar</span>
                                      </button>
                                    )}

                                    {/* Botão Editar/Cancelar */}
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        console.log('Clicou em editar versão:', versao.id, 'Status:', versao.status);
                                        if (selectedVersion === versao.id) {
                                          handleCancelarEdicao();
                                        } else {
                                          handleEditarVersao(versao.id);
                                        }
                                      }}
                                      className={`${versao.status === 'pendente' && !selectedVersion ? 'flex-1' : 'px-2'} py-1.5 rounded text-xs font-medium transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center space-x-1 ${
                                        selectedVersion === versao.id
                                          ? 'bg-red-50 hover:bg-red-100 text-red-700'
                                          : 'bg-blue-50 hover:bg-blue-100 text-blue-700'
                                      }`}
                                      title={selectedVersion === versao.id ? 'Cancelar edição' : 'Editar esta versão'}
                                    >
                                      <div className="w-3 h-3 flex items-center justify-center">
                                        <i className={selectedVersion === versao.id ? 'ri-close-line' : 'ri-edit-line'}></i>
                                      </div>
                                      <span>{selectedVersion === versao.id ? 'Cancelar' : 'Editar'}</span>
                                    </button>

                                    {/* Botão Salvar Alterações - aparece quando esta versão está sendo editada */}
                                    {selectedVersion === versao.id && (
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleSalvarVersao();
                                        }}
                                        className="px-2 py-1.5 bg-blue-600 text-white rounded text-xs font-medium hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center space-x-1"
                                      >
                                        <div className="w-3 h-3 flex items-center justify-center">
                                          <i className="ri-save-line"></i>
                                        </div>
                                        <span>Salvar</span>
                                      </button>
                                    )}
                                  </div>
                                </div>
                              )}

                              {/* Mensagem para versões publicadas */}
                              {versao.status === 'publicado' && (
                                <div className="pl-11">
                                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                                    <div className="flex items-center space-x-2">
                                      <div className="w-4 h-4 flex items-center justify-center">
                                        <i className="ri-eye-line text-green-600"></i>
                                      </div>
                                      <span className="text-xs text-green-700">
                                        Clique para visualizar esta versão publicada
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-3">
                          <div className="w-6 h-6 flex items-center justify-center">
                            <i className="ri-history-line text-gray-400 text-xl"></i>
                          </div>
                        </div>
                        <p className="text-sm text-gray-500">Nenhuma versão salva</p>
                      </div>
                    )}

                    {/* Botão Cancelar Edição - aparece quando uma versão está selecionada */}
                    {selectedVersion && (
                      <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <div className="w-4 h-4 flex items-center justify-center">
                              <i className="ri-information-line text-blue-600"></i>
                            </div>
                            <span className="text-sm text-blue-800">
                              {(() => {
                                const versao = historicoRegulamento.find(v => v.id === selectedVersion);
                                return versao?.status === 'publicado' 
                                  ? `Visualizando versão ${versao.versao} (publicada)`
                                  : `Editando versão ${versao?.versao}`;
                              })()}
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={handleCancelarEdicao}
                            className="px-3 py-1.5 bg-white border border-blue-300 text-blue-700 rounded text-xs font-medium hover:bg-blue-50 transition-colors cursor-pointer whitespace-nowrap"
                          >
                            {(() => {
                              const versao = historicoRegulamento.find(v => v.id === selectedVersion);
                              return versao?.status === 'publicado' ? 'Fechar' : 'Cancelar';
                            })()}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Footer Info */}
                  <div className="p-3 border-t border-gray-200 bg-white rounded-b-lg">
                    <div className="flex items-start space-x-2">
                      <div className="w-4 h-4 flex items-center justify-center mt-0.5">
                        <i className="ri-information-line text-blue-600 text-sm"></i>
                      </div>
                      <p className="text-xs text-gray-600">
                        Cada alteração salva cria uma nova versão do regulamento
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <div className="w-5 h-5 flex items-center justify-center mt-0.5">
                <i className="ri-information-line text-blue-600"></i>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-blue-900 mb-1">Dica</h4>
                <p className="text-sm text-blue-800">
                  Após preencher os dados básicos, você poderá configurar os objetivos, ações, regras e premiações da campanha nas próximas etapas.
                </p>
              </div>
            </div>
          </div>
        </form>

        {/* Action Buttons */}
        <div className="flex items-center justify-between">
          <button 
            onClick={handleVoltar}
            className="px-6 py-2 text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            Voltar
          </button>
          <button 
            onClick={handleProximo}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors cursor-pointer whitespace-nowrap flex items-center space-x-2"
          >
            <span>Próximo: Objetivos e Metas</span>
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line"></i>
            </div>
          </button>
        </div>
      </div>
    </Layout>
  );
}

// Componente para formulário de edição de FAQ
function FaqEditForm({ 
  item, 
  onSave, 
  onCancel, 
  formatBold, 
  formatItalic, 
  formatUnderline, 
  insertList, 
  insertNumberedList, 
  insertLink 
}: {
  item: any;
  onSave: (id: number, pergunta: string, resposta: string) => void;
  onCancel: (id: number) => void;
  formatBold: (id: string) => void;
  formatItalic: (id: string) => void;
  formatUnderline: (id: string) => void;
  insertList: (id: string) => void;
  insertNumberedList: (id: string) => void;
  insertLink: (id: string) => void;
}) {
  const [perguntaEdit, setPerguntaEdit] = useState(item.pergunta);
  const [respostaEdit, setRespostaEdit] = useState(item.resposta);

  const handleSave = () => {
    onSave(item.id, perguntaEdit, respostaEdit);
  };

  const handleCancel = () => {
    setPerguntaEdit(item.pergunta);
    setRespostaEdit(item.resposta);
    onCancel(item.id);
  };

  const textareaId = `faq-edit-${item.id}`;

  return (
    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
      <div className="flex items-center justify-between mb-4">
        <h4 className="text-lg font-semibold text-yellow-900">Editando FAQ</h4>
        <button
          type="button"
          onClick={handleCancel}
          className="text-yellow-400 hover:text-yellow-600 cursor-pointer"
        >
          <div className="w-6 h-6 flex items-center justify-center">
            <i className="ri-close-line text-xl"></i>
          </div>
        </button>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-yellow-900 mb-2">
            Pergunta *
          </label>
          <input
            type="text"
            value={perguntaEdit}
            onChange={(e) => setPerguntaEdit(e.target.value)}
            className="w-full px-4 py-2 border border-yellow-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 text-sm"
            placeholder="Digite a pergunta..."
            maxLength={200}
          />
          <div className="text-xs text-yellow-600 mt-1">
            {perguntaEdit.length}/200 caracteres
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-yellow-900 mb-2">
            Resposta *
          </label>
          
          {/* Toolbar de Formatação */}
          <div className="border border-yellow-300 rounded-lg p-3 bg-white mb-2">
            <div className="flex items-center space-x-2 flex-wrap gap-2">
              <button
                type="button"
                onClick={() => formatBold(textareaId)}
                className="px-3 py-2 bg-white border border-gray-300 rounded hover:bg-gray-50 text-sm font-bold cursor-pointer whitespace-nowrap flex items-center space-x-1"
                title="Negrito"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-bold"></i>
                </div>
                <span className="text-xs">B</span>
              </button>
              <button
                type="button"
                onClick={() => formatItalic(textareaId)}
                className="px-3 py-2 bg-white border border-gray-300 rounded hover:bg-gray-50 text-sm italic cursor-pointer whitespace-nowrap flex items-center space-x-1"
                title="Itálico"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-italic"></i>
                </div>
                <span className="text-xs">I</span>
              </button>
              <button
                type="button"
                onClick={() => formatUnderline(textareaId)}
                className="px-3 py-2 bg-white border border-gray-300 rounded hover:bg-gray-50 text-sm underline cursor-pointer whitespace-nowrap flex items-center space-x-1"
                title="Sublinhado"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-underline"></i>
                </div>
                <span className="text-xs">U</span>
              </button>
              <div className="w-px h-6 bg-gray-300"></div>
              <button
                type="button"
                onClick={() => insertList(textareaId)}
                className="px-3 py-2 bg-white border border-gray-300 rounded hover:bg-gray-50 text-sm cursor-pointer whitespace-nowrap flex items-center space-x-1"
                title="Lista com Marcadores"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-list-unordered"></i>
                </div>
              </button>
              <button
                type="button"
                onClick={() => insertNumberedList(textareaId)}
                className="px-3 py-2 bg-white border border-gray-300 rounded hover:bg-gray-50 text-sm cursor-pointer whitespace-nowrap flex items-center space-x-1"
                title="Lista Numerada"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-list-ordered"></i>
                </div>
              </button>
              <button
                type="button"
                onClick={() => insertLink(textareaId)}
                className="px-3 py-2 bg-white border border-gray-300 rounded hover:bg-gray-50 text-sm cursor-pointer whitespace-nowrap flex items-center space-x-1"
                title="Inserir Link"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-link"></i>
                </div>
              </button>
            </div>
          </div>

          <textarea
            id={textareaId}
            value={respostaEdit}
            onChange={(e) => setRespostaEdit(e.target.value)}
            rows={6}
            className="w-full px-4 py-3 border border-yellow-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 text-sm font-mono resize-none"
            placeholder="Digite a resposta..."
            maxLength={1000}
          />
          <div className="text-xs text-yellow-600 mt-1">
            {respostaEdit.length}/1000 caracteres
          </div>
        </div>

        <div className="flex items-center justify-end space-x-3 pt-4">
          <button
            type="button"
            onClick={handleCancel}
            className="px-4 py-2 text-yellow-700 bg-white border border-yellow-300 hover:bg-yellow-50 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!perguntaEdit.trim() || !respostaEdit.trim()}
            className="px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Salvar Alterações
          </button>
        </div>
      </div>
    </div>
  );
}
